import ScrollModal from "@/components/common/scroll-modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  FileDropZone,
  FileItem,
  Heading,
  Icon,
  TextAreaField,
  useFileDropZone,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  type SuspensionRequestFormData,
  suspensionRequestSchema,
} from "@/schemas/procedures/suspension";
import {
  useCreateSuspensionRequest,
  type SuspensionInfo,
} from "@/queries/deadline-suspensions/deadline-suspensions.api";
import { useState } from "react";
import { uploadFile } from "@/queries/process";

interface SuspensionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (shouldRefetch: boolean) => void;
  tramiteId: string;
  suspensionRequest?: SuspensionInfo;
}

export const SuspensionModal = ({
  open,
  onClose,
  onSubmit,
  tramiteId,
  suspensionRequest,
}: SuspensionModalProps) => {
  const {
    register,
    handleSubmit,
    clearErrors,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SuspensionRequestFormData>({
    resolver: zodResolver(suspensionRequestSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      reason: "",
      document: null,
    } as SuspensionRequestFormData,
  });
  const { showToast } = useToast();
  const {
    files,
    invalidFiles,
    isDragging,
    setIsDragging,
    setFiles,
    setInvalidFiles,
    handleFileChange,
    handleDrop,
  } = useFileDropZone({
    maxFileSizeBytes: 10 * 1024 * 1024,
    acceptedFileMimeTypes: ["application/pdf", "image/jpeg", "image/png"],
    cb: (validFiles) => {
      if (validFiles.length > 0) {
        const file = validFiles[0];
        setValue("document", file, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setInvalidFiles([]);
      }
    },
  });
  const { mutateAsync: createRequest } = useCreateSuspensionRequest();
  const [isLoading, setIsLoading] = useState(false);

  const closeOnSubmit = () => {
    onSubmit(true);
    setIsLoading(false);
    clearForm();
  };

  const onSubmitHandler = async (data: SuspensionRequestFormData) => {
    try {
      setIsLoading(true);
      const response = await createRequest({
        formality_request_id: tramiteId as string,
        requested_days: suspensionRequest?.data?.max_days ?? 0,
        observation: data.reason,
      });
      if (!response) {
        throw new Error("Error al crear la solicitud");
      }

      // Upload file
      if (data.document) {
        try {
          await uploadFile({
            file: data.document,
            entity_id: response.data.id,
            entity_type: "node_deadline_suspension_request",
            collection: "node_deadline_suspension_request",
          });
        } catch (uploadErr) {
          const errorMessage =
            uploadErr instanceof Error
              ? uploadErr.message
              : "Error al subir archivo";
          console.error("Error uploading file:", errorMessage);

          showToast({
            title: "Solicitud enviada",
            description:
              "La solicitud de suspensión fue enviada con éxito pero hubo un error al subir el archivo",
            options: {
              duration: 5000,
              type: "warning",
            },
          });
          closeOnSubmit();
          return;
        }
      }

      // Success path - only reached if no upload error
      showToast({
        title: "Solicitud enviada",
        description: "La solicitud de suspensión fue enviada con éxito",
        options: {
          duration: 5000,
          type: "success",
        },
      });
      closeOnSubmit();
    } catch (error) {
      // Only for request creation errors
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      console.error("Error creating suspension request:", errorMessage);

      setIsLoading(false);
      clearForm();
      onSubmit(false);

      showToast({
        title: "Error al enviar la suspensión",
        description: "No se ha podido enviar la solicitud. Intente nuevamente",
        options: {
          duration: 5000,
          type: "error",
        },
      });
    }
  };

  const clearForm = () => {
    clearErrors();
    setValue("reason", "");
    setValue("document", null);
    setFiles([]);
    setInvalidFiles([]);
  };

  const handleCloseModal = () => {
    clearForm();
    onClose();
  };

  const reasonText = watch("reason");

  const charCount = reasonText?.length || 0;
  const maxChars = 120;
  const currentFile = files[0];

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleCloseModal}
      modalContentClassName="w-full max-w-[340px] md:max-w-[520px] max-h-[680px] flex flex-col p-8 gap-8"
      header={
        <div className="flex flex-col justify-center items-center">
          <Heading variant="h2" className="mb-0!">
            Solicitar suspensión
          </Heading>
          <p className="text-center text-body leading-3xl">
            Especifica las razones por las cuales deseas solicitar una
            suspensión para tu proceso.
          </p>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmitHandler)}
        className="flex flex-col gap-8"
      >
        <div>
          <TextAreaField
            id="reason"
            count={charCount}
            label="¿Por qué necesitas más tiempo para completar esta etapa?"
            required
            limit={maxChars}
            maxLength={maxChars}
            error={errors.reason?.message}
            placeholder="Escribe tu comentario..."
            {...register("reason")}
          />
          <div className="mt-2">
            <div className="flex justify-between">
              <p className="text-sm leading-2xl font-semibold text-body-loud">
                Documento justificante
              </p>
              <span className="text-sm leading-2xl font-normal text-body-quiet">
                Opcional
              </span>
            </div>
            {!currentFile && (
              <FileDropZone
                isDragging={isDragging}
                onFileDrop={handleDrop}
                onZoneClick={handleFileChange}
                setIsDragging={setIsDragging}
                acceptedFilesMimeTypes={[
                  "application/pdf",
                  "image/jpeg",
                  "image/png",
                ]}
                acceptedFilesExtensions={[".pdf", ".jpg", ".jpeg", ".png"]}
              />
            )}
            {errors.document && (
              <p className="text-danger text-xs leading-xl font-normal">
                <Icon type="far" name="circle-xmark" className="mr-1.5" />
                {errors.document.message}
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {Array.isArray(files) &&
                files.length > 0 &&
                files.map((file: File) => (
                  <FileItem
                    key={file.name}
                    fileName={file.name}
                    action={<Icon type="far" name="trash" size="lg" />}
                    tag={`${Math.round(file.size / 1024)} KB`}
                    onAction={() => {
                      setValue("document", null);
                      setFiles([]);
                    }}
                  />
                ))}
            </div>
            {invalidFiles.length > 0 && (
              <div className="text-warning">
                <ul>
                  {invalidFiles.map((file) => (
                    <li
                      key={file.name}
                      className="text-danger text-xs leading-xl font-normal"
                    >
                      <Icon type="far" name="circle-xmark" className="mr-1.5" />
                      {file.name} - Archivo no válido
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="w-full grid grid-cols-2 gap-3">
          <Button
            variant="tertiary"
            size="lg"
            type="button"
            onClick={handleCloseModal}
            className="min-w-30 cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="lg"
            type="submit"
            disabled={isSubmitting || isLoading || !reasonText}
            className="min-w-35 cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
          >
            {isSubmitting ? "Enviando..." : "Enviar a lider"}
          </Button>
        </div>
      </form>
    </ScrollModal>
  );
};
